// Extracted verbatim from the v3 monolith (see BIRDSEYE.md module split plan).

import { DATA_WRAPPER, DATA_LIST, DATA_ITEM } from './constants.js';
import { discoverList } from './discovery.js';
import { toArray, toInt, dispatchEventSafe, fetchWithRetry, normalizeUrl, pageFromUrl, stripFirstPageParam } from './utils.js';
import { setLoadingState, setProgressState } from './ui-state.js';

export var _shouldEscalateSearch = function (state) {
  if (!this.searchEscalate || this.loadMode !== 'auto') {
    return false;
  }

  if (this.searchEscalationInProgress || this.datasetComplete || this.isFetching || this.autoLoadInProgress) {
    return false;
  }

  if (!state || !state.searchGroups || !state.searchGroups.length) {
    return false;
  }

  return state.searchGroups.some(function (group) {
    return String(group.raw || '').trim().length >= this.searchEscalateMin;
  }, this);
};

export var _escalateSearchLoadAll = function (state) {
  var self = this;

  if (this.searchEscalationInProgress || this.datasetComplete || this.loadMode !== 'auto') {
    return Promise.resolve();
  }

  this.searchEscalationInProgress = true;
  setProgressState(this, true, this.searchEscalateText);
  this._debugLog('search-escalate:start', {
    searchGroups: state.searchGroups.length,
    pendingPages: this.pendingPageUrls.length,
    items: this.items.length,
  });

  dispatchEventSafe(this.wrapper, 'panda:search:escalate:start', {
    searchGroups: state.searchGroups.length,
    pendingPages: this.pendingPageUrls.length,
    loadedItems: this.items.length,
  });

  return this.loadAllPages({ silent: false }).finally(function () {
    self.searchEscalationInProgress = false;
    setProgressState(self, false);
    self._debugLog('search-escalate:end', {
      pendingPages: self.pendingPageUrls.length,
      items: self.items.length,
    });
    dispatchEventSafe(self.wrapper, 'panda:search:escalate:end', {
      pendingPages: self.pendingPageUrls.length,
      loadedItems: self.items.length,
    });

    self.applyFilters({
      resetPage: false,
      reason: 'search-escalate',
      skipAutoEnsure: true,
      skipSearchEscalate: true,
    });
  });
};

export var _discoverPaginationUrls = function (doc) {
  var root = this._resolveWrapperInDocument(doc);
  var links = toArray(root.querySelectorAll('.w-pagination-wrapper a[href], .w-pagination-next[href], a.w-pagination-next[href], a[aria-label*="Page"][href]'));
  var self = this;

  links
    .map(function (link) {
      return link.getAttribute('href');
    })
    .filter(Boolean)
    .map(function (href) {
      return normalizeUrl(href);
    })
    .forEach(function (url) {
      self._enqueuePageUrl(url);
    });

  this.pendingPageUrls.sort(function (a, b) {
    return pageFromUrl(a) - pageFromUrl(b);
  });

  this._debugLog('pagination:discovered', {
    pendingPages: this.pendingPageUrls.length,
    preview: this.pendingPageUrls.slice(0, 5),
  });

  this._syncDatasetStatus('discover');
  this._refreshLoadButtonState();
};

export var _syncDatasetStatus = function (reason, context) {
  this.datasetComplete = this.pendingPageUrls.length === 0;

  var detail = Object.assign(
    {
      reason: reason || 'unknown',
      loadedItems: this.items.length,
      loadedPages: this.loadedPageUrls.size,
      pendingPages: this.pendingPageUrls.length,
      failedPages: this.failedPageUrls.size,
      complete: this.datasetComplete,
    },
    context || {}
  );

  dispatchEventSafe(this.wrapper, 'panda:dataset:progress', detail);

  if (this.datasetComplete) {
    this.datasetPrimed = true;
    if (!this._datasetCompleteEmitted) {
      this._datasetCompleteEmitted = true;
      this._debugLog('dataset:complete', detail);
      dispatchEventSafe(this.wrapper, 'panda:dataset:complete', detail);
    }
  } else {
    this._datasetCompleteEmitted = false;
  }
};

export var _enqueuePageUrl = function (url) {
  var normalized = normalizeUrl(url);

  if (this.loadedPageUrls.has(normalized)) {
    return;
  }

  if (this.pendingPageUrls.indexOf(normalized) !== -1) {
    return;
  }

  // The page already in the DOM — including its `?x_page=1` alias — is never fetched again.
  if (stripFirstPageParam(normalized) === stripFirstPageParam(window.location.href)) {
    return;
  }

  this.pendingPageUrls.push(normalized);
};

export var _resolveWrapperInDocument = function (doc) {
  var wrappers = toArray(doc.querySelectorAll(DATA_WRAPPER));

  if (wrappers.length > this.wrapperIndex && this.wrapperIndex >= 0) {
    return wrappers[this.wrapperIndex];
  }

  if (this.wrapper.id) {
    var byId = doc.getElementById(this.wrapper.id);
    if (byId) {
      return byId;
    }
  }

  if (wrappers.length) {
    return wrappers[0];
  }

  return doc;
};

export var _extractItemsFromDocument = function (doc) {
  var wrapper = this._resolveWrapperInDocument(doc);
  var list = wrapper.querySelector(DATA_LIST);

  if (!list) {
    var lists = toArray(doc.querySelectorAll(DATA_LIST));
    if (lists.length > this.listIndex && this.listIndex >= 0) {
      list = lists[this.listIndex];
    } else if (lists.length) {
      list = lists[0];
    }
  }

  // v4: when the live list was inferred, apply the same discovery to fetched pages.
  if (!list && this.discovery && this.discovery.list.source === 'inferred') {
    var discovered = discoverList(wrapper === doc ? doc.body || doc : wrapper);
    list = discovered.node;
  }

  if (!list) {
    return [];
  }

  return toArray(list.querySelectorAll(this.itemSelector || DATA_ITEM));
};

export var _refreshLoadButtonState = function () {
  if (!this.loadButton) {
    return;
  }

  if (this.loadMode !== 'manual') {
    this.loadButton.hidden = true;
    return;
  }

  var hasMore = this.pendingPageUrls.length > 0;
  this.loadButton.hidden = !hasMore;
  var blocked = !hasMore || this.isLoading || this.isFetching;
  this.loadButton.disabled = blocked;
  this.loadButton.setAttribute('aria-disabled', blocked ? 'true' : 'false');
};

export var loadNextPage = function (opts) {
  var self = this;
  var options = opts || {};
  var silent = !!options.silent;

  if (this.isFetching) {
    return Promise.resolve({ loaded: 0, url: null });
  }

  if (!this.pendingPageUrls.length) {
    this._refreshLoadButtonState();
    this._debugLog('load-next:skip:no-pending-pages');
    this._syncDatasetStatus('load-next:skip');
    return Promise.resolve({ loaded: 0, url: null });
  }

  var url = this.pendingPageUrls.shift();
  this.loadedPageUrls.add(url);
  this.isFetching = true;
  if (!silent) {
    setLoadingState(this, true);
  }
  this._debugLog('load-next:start', {
    url: url,
    pendingAfterPop: this.pendingPageUrls.length,
    loadedPages: this.loadedPageUrls.size,
    silent: silent,
  });

  return fetchWithRetry(url, {
    retries: toInt(this.wrapper.getAttribute('data-pf-load-retries'), 2),
    timeout: toInt(this.wrapper.getAttribute('data-pf-load-timeout'), 12000),
  })
    .then(function (html) {
      var parser = new DOMParser();
      var nextDoc = parser.parseFromString(html, 'text/html');
      var importedNodes = self._extractItemsFromDocument(nextDoc);
      var fragment = document.createDocumentFragment();
      var loadedCount = 0;
      var appendedCount = 0;

      importedNodes.forEach(function (node) {
        var imported = document.importNode(node, true);
        imported.__pfNeedsIxInit = true;
        var added = self._addItemNode(imported);
        if (!added) {
          return;
        }

        if (!self.mountVisibleOnly) {
          imported.style.display = 'none';
          imported.hidden = true;
          fragment.appendChild(imported);
          appendedCount += 1;
        }
        loadedCount += 1;
      });

      if (appendedCount > 0) {
        self.list.appendChild(fragment);
        if (window.Webflow && typeof window.Webflow.require === 'function') {
          try {
            var ix2 = window.Webflow.require('ix2');
            if (ix2 && typeof ix2.init === 'function') {
              ix2.init();
            }
          } catch (_error) {
            // Optional Webflow re-init; ignore when unavailable.
          }
        }
      }

      self._discoverPaginationUrls(nextDoc);
      self._debugLog('load-next:success', {
        url: url,
        loadedItems: loadedCount,
        appendedItems: appendedCount,
        totalItems: self.items.length,
        pendingPages: self.pendingPageUrls.length,
      });

      dispatchEventSafe(self.wrapper, 'panda:load:page', {
        url: url,
        loaded: loadedCount,
        totalLoaded: self.items.length,
        pending: self.pendingPageUrls.length,
      });

      self.applyFilters({
        resetPage: false,
        reason: 'load',
        skipAutoEnsure: true,
      });

      return {
        loaded: loadedCount,
        url: url,
      };
    })
    .catch(function (error) {
      self.failedPageUrls.add(url);
      console.error('[PandaFilter] Failed loading page:', url, error);
      self._debugLog('load-next:error', {
        url: url,
        message: String(error && error.message ? error.message : error),
        pendingPages: self.pendingPageUrls.length,
      });
      dispatchEventSafe(self.wrapper, 'panda:load:error', {
        url: url,
        error: String(error && error.message ? error.message : error),
      });

      self._syncDatasetStatus('load-next:error', {
        url: url,
      });

      return {
        loaded: 0,
        url: url,
        error: error,
      };
    })
    .finally(function () {
      self.isFetching = false;
      if (!silent) {
        setLoadingState(self, false);
      }
      self._refreshLoadButtonState();
    });
};

export var _loadUntilItemCount = function (targetCount, reason, opts) {
  var self = this;
  var guard = 0;
  var target = Math.max(0, targetCount || 0);
  var options = opts || {};
  var silent = !!options.silent;
  var loadPageOptions = Object.assign({}, options, { silent: true });

  if (!silent) {
    setLoadingState(this, true);
  }

  function loop() {
    guard += 1;
    if (guard > 250) {
      self._debugLog('load-until:guard-stop', { guard: guard, target: target, reason: reason });
      return Promise.resolve();
    }

    if (self.items.length >= target || !self.pendingPageUrls.length) {
      self._debugLog('load-until:done', {
        reason: reason,
        target: target,
        loadedItems: self.items.length,
        pendingPages: self.pendingPageUrls.length,
      });
      return Promise.resolve();
    }

    return self.loadNextPage(loadPageOptions).then(loop);
  }

  return loop().finally(function () {
    if (!silent) {
      setLoadingState(self, false);
    }
  });
};

export var _primeInitialDataset = function () {
  var target = Math.max(this.pageSize, this.initialItemsTarget);

  if (this.loadMode !== 'auto' || this.loadStrategy !== 'balanced' || target <= 0) {
    return Promise.resolve();
  }

  if (this.items.length >= target || this.pendingPageUrls.length === 0) {
    this.datasetPrimed = true;
    this._debugLog('dataset:prime:skip', {
      target: target,
      items: this.items.length,
      pendingPages: this.pendingPageUrls.length,
    });
    return Promise.resolve();
  }

  this._debugLog('dataset:prime:start', {
    target: target,
    items: this.items.length,
    pendingPages: this.pendingPageUrls.length,
  });

  return this._loadUntilItemCount(target, 'prime', { silent: true }).finally(
    function () {
      this.datasetPrimed = true;
      this._debugLog('dataset:prime:end', {
        target: target,
        items: this.items.length,
        pendingPages: this.pendingPageUrls.length,
      });
    }.bind(this)
  );
};

export var loadAllPages = function (opts) {
  var self = this;
  var guard = 0;
  var options = opts || {};
  var silent = !!options.silent;
  var loadPageOptions = Object.assign({}, options, { silent: true });
  this._debugLog('load-all:start', { pendingPages: this.pendingPageUrls.length });

  if (!silent) {
    setLoadingState(this, true);
  }

  function loop() {
    guard += 1;
    if (guard > 250) {
      self._debugLog('load-all:guard-stop', { guard: guard });
      return Promise.resolve();
    }

    if (!self.pendingPageUrls.length) {
      self._debugLog('load-all:done', { totalItems: self.items.length });
      return Promise.resolve();
    }

    return self.loadNextPage(loadPageOptions).then(function () {
      return loop();
    });
  }

  return loop().finally(function () {
    if (!silent) {
      setLoadingState(self, false);
    }
  });
};

export var ensureEnoughItemsForCurrentView = function (state) {
  var self = this;

  if (this.autoLoadInProgress || this.loadMode !== 'auto' || this.loadStrategy === 'full') {
    this._debugLog('auto-load:skip', {
      inProgress: this.autoLoadInProgress,
      loadMode: this.loadMode,
      loadStrategy: this.loadStrategy,
    });
    return Promise.resolve();
  }

  this.autoLoadInProgress = true;

  var required = this.currentPage * this.pageSize;
  var hasActiveCriteria = false;
  if (state) {
    hasActiveCriteria =
      Object.keys(state.filtersByField || {}).length > 0 ||
      (state.searchGroups || []).length > 0;
  }
  if (this.loadStrategy === 'balanced' && !this.datasetPrimed && !hasActiveCriteria) {
    required = Math.max(required, Math.max(this.pageSize, this.initialItemsTarget));
  }

  var guard = 0;
  var showLoadingWhileSearching =
    this.filteredItems.length === 0 &&
    this.pendingPageUrls.length > 0;

  if (showLoadingWhileSearching) {
    setLoadingState(this, true);
  }

  this._debugLog('auto-load:start', {
    required: required,
    filtered: this.filteredItems.length,
    pendingPages: this.pendingPageUrls.length,
    loadStrategy: this.loadStrategy,
    datasetPrimed: this.datasetPrimed,
    hasActiveCriteria: hasActiveCriteria,
  });

  function loop() {
    guard += 1;
    if (guard > 100) {
      self._debugLog('auto-load:guard-stop', { guard: guard });
      return Promise.resolve();
    }

    if (!self.pendingPageUrls.length) {
      self._debugLog('auto-load:done:no-pending', {
        required: required,
        filtered: self.filteredItems.length,
      });
      return Promise.resolve();
    }

    // If filtered count is exactly page size, there may still be more pages.
    // Load at least one extra page until filteredItems becomes strictly greater.
    if (self.filteredItems.length > required) {
      self._debugLog('auto-load:done:enough', {
        required: required,
        filtered: self.filteredItems.length,
      });
      return Promise.resolve();
    }

    return self.loadNextPage({ silent: true }).then(function () {
      return loop();
    });
  }

  return loop().finally(function () {
    if (
      self.loadStrategy === 'balanced' &&
      (self.items.length >= Math.max(self.pageSize, self.initialItemsTarget) ||
        self.pendingPageUrls.length === 0)
    ) {
      self.datasetPrimed = true;
    }
    self.autoLoadInProgress = false;
    if (showLoadingWhileSearching) {
      setLoadingState(self, false);
    }
    self._debugLog('auto-load:finally', {
      required: required,
      filtered: self.filteredItems.length,
      pendingPages: self.pendingPageUrls.length,
    });
  });
};

